import { produce } from 'immer';
import _ from 'lodash';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ModalCheckImg from '../../../../../common/assets/images/merchants/modal-check.svg';
import { ProductStatusTypes } from '../../../../../common/enums';
import {
  getRequiredValidationError,
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../common/global/helpers';
import useMount from '../../../../../common/hooks/useMount';
import Button from '../../../../../common/widgets/Button';
import Dialog from '../../../../../common/widgets/Dialog';
import { ErrorPanel } from '../../../../../common/widgets/ErrorPanel';
import Loader from '../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../common/widgets/LoadingSpinner';
import { ErrorState, ErrorType } from '../../../../benefit/models/Errors';
import { emailRegex, onlyDigitsRegex } from '../../../constants';
import { AccountInfoContext } from '../../../contexts/AccountInfoContext';
import { AddProductSteps, ProductTypes } from '../../../enums';
import { CountyFilterModel } from '../../../models/filters';
import { ProductStatusModel, ProductTypeModel } from '../../../models/products';
import { resources } from '../../../resources/resources';
import { filtersService } from '../../../services/filters';
import { productsService } from '../../../services/products';
import {
  descriptionField,
  emailField,
  howToOrderField,
  nameField,
  phoneNumberField,
  ProductContext,
} from '../Common/constants';
import {
  GiftCardOfferDetails,
  GiftCardOfferDetailsForm,
} from '../Common/OfferDetails/GiftCard';
import {
  PercentageVoucherOfferDetails,
  PercentageVoucherOfferDetailsForm,
} from '../Common/OfferDetails/PercentageVoucher';
import {
  ProductOrServiceOfferDetails,
  ProductOrServiceOfferDetailsForm,
} from '../Common/OfferDetails/ProductOrService';
import {
  ValueVoucherOfferDetails,
  ValueVoucherOfferDetailsForm,
} from '../Common/OfferDetails/ValueVoucher';
import { ProductDetails } from '../Common/ProductDetails';
import { ProductTypeSelector } from '../Common/ProductTypeSelector';
import { ProgressBar } from '../Common/ProgressBar';
import { Translations } from '../Common/Translations';
import {
  ProductDetailsForm,
  getAddProductCommand,
  getEnName,
  getRoName,
  setProductSentForReviewInfoMessage,
  validate,
} from '../Common/utils';

export const AddProductPage = () => {
  const [activeStep, setActiveStep] = useState<AddProductSteps>(
    AddProductSteps.ProductType,
  );
  const [productTypes, setProductTypes] = useState<
    ProductTypeModel[] | undefined
  >();
  const [productStatuses, setProductStatuses] = useState<
    ProductStatusModel[] | undefined
  >();
  const [countiesWithCities, setCountiesWithCities] = useState<
    CountyFilterModel[] | undefined
  >();
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(true);
  const [productType, setProductType] = useState<ProductTypes | undefined>();
  const [detailsForm, setDetailsForm] = useState<ProductDetailsForm>({
    productDescriptions: {
      ro: {
        name: { name: getRoName(nameField) },
        description: { name: getRoName(descriptionField) },
        howToOrder: { name: getRoName(howToOrderField) },
      },
      en: {
        name: { name: getEnName(nameField) },
        description: { name: getEnName(descriptionField) },
        howToOrder: { name: getEnName(howToOrderField) },
      },
    },
  });
  const [
    productOrServiceOfferDetailsForm,
    setProductOrServiceOfferDetailsForm,
  ] = useState<ProductOrServiceOfferDetailsForm>({
    codes: { generated: [], manuallyAdded: { valid: [], invalid: [] } },
    isCourse: false,
  });
  const [giftCardOfferDetailsForm, setGiftCardOfferDetailsForm] =
    useState<GiftCardOfferDetailsForm>({
      codes: { generated: [], manuallyAdded: { valid: [], invalid: [] } },
      orderInfo: false,
    });
  const [valueVoucherOfferDetailsForm, setValueVoucherOfferDetailsForm] =
    useState<ValueVoucherOfferDetailsForm>({});
  const [
    percentageVoucherOfferDetailsForm,
    setPercentageVoucherOfferDetailsForm,
  ] = useState<PercentageVoucherOfferDetailsForm>({});
  const [isSaveButtonLoading, setIsSaveButtonLoading] =
    useState<boolean>(false);
  const [isSaveAsDraftButtonLoading, setIsSaveAsDraftButtonLoading] =
    useState<boolean>(false);
  const [isSendForReviewButtonLoading, setIsSendForReviewButtonLoading] =
    useState<boolean>(false);
  const [productId, setProductId] = useState<number>();
  const [errors, setErrors] = useState<ErrorState>({});
  const [showConfirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false);
  const { accountInfo } = useContext(AccountInfoContext);
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  useMount(abortController => {
    const loadProductTypes = async () => {
      setShowLoading(true);
      const resp = await productsService.getProductTypes({
        signal: abortController?.signal,
      });
      if (abortController?.signal.aborted) return;
      setShowLoading(false);
      if (resp.hasErrors) {
        setShowErrorPanel(true);
      } else {
        setProductTypes(resp.data);
      }
    };
    const loadProductStatuses = async () => {
      setShowLoading(true);
      const resp = await productsService.getProductStatuses({
        signal: abortController?.signal,
      });
      if (abortController?.signal.aborted) return;
      setShowLoading(false);
      if (resp.hasErrors) {
        setShowErrorPanel(true);
      } else {
        setProductStatuses(resp.data);
      }
    };
    const loadCountiesWithCities = async () => {
      setShowLoading(true);
      const resp = await filtersService.getCountiesWithCities({
        signal: abortController?.signal,
      });
      if (abortController?.signal.aborted) return;
      setShowLoading(false);
      if (resp.hasErrors) {
        setShowErrorPanel(true);
      } else {
        setCountiesWithCities(resp.data);
      }
    };
    loadProductTypes();
    loadProductStatuses();
    loadCountiesWithCities();
  });

  const partiallyValidate = (
    validateEnglishFields: boolean,
  ): { isValid: boolean } => {
    const result = { isValid: true };
    let newErrors: ErrorState = {};
    if (!detailsForm.productDescriptions.ro.name.value) {
      newErrors = produce(newErrors, d => {
        d[getRoName(nameField)] = [
          getRequiredValidationError(getRoName(nameField)),
        ];
      });
      result.isValid = false;
    }
    if (
      detailsForm.productDescriptions.ro.email?.value &&
      !emailRegex.test(detailsForm.productDescriptions.ro.email?.value)
    ) {
      result.isValid = false;
      newErrors = produce(newErrors, d => {
        d[getRoName(emailField)] = [
          {
            code: resources.validation.invalidEmail,
            target: getRoName(emailField),
            placeholders: {},
            severity: 'error',
          },
        ];
      });
    }
    if (
      detailsForm.productDescriptions.ro.phoneNumber?.value &&
      !onlyDigitsRegex.test(
        detailsForm.productDescriptions.ro.phoneNumber.value,
      )
    ) {
      result.isValid = false;
      newErrors = produce(newErrors, d => {
        d[getRoName(phoneNumberField)] = [
          {
            code: resources.validation.invalidPhoneNumber,
            target: getRoName(phoneNumberField),
            placeholders: {},
            severity: 'error',
          },
        ];
      });
    }
    if (validateEnglishFields) {
      if (!detailsForm.productDescriptions.en.name.value) {
        newErrors = produce(newErrors, d => {
          d[getEnName(nameField)] = [
            getRequiredValidationError(getEnName(nameField)),
          ];
        });
        result.isValid = false;
      }
      if (
        detailsForm.productDescriptions.en.email?.value &&
        !emailRegex.test(detailsForm.productDescriptions.en.email?.value)
      ) {
        result.isValid = false;
        newErrors = produce(newErrors, d => {
          d[getEnName(emailField)] = [
            {
              code: resources.validation.invalidEmail,
              target: getEnName(emailField),
              placeholders: {},
              severity: 'error',
            },
          ];
        });
      }
      if (
        detailsForm.productDescriptions.en.phoneNumber?.value &&
        !onlyDigitsRegex.test(
          detailsForm.productDescriptions.en.phoneNumber.value,
        )
      ) {
        result.isValid = false;
        newErrors = produce(newErrors, d => {
          d[getEnName(phoneNumberField)] = [
            {
              code: resources.validation.invalidPhoneNumber,
              target: getEnName(phoneNumberField),
              placeholders: {},
              severity: 'error',
            },
          ];
        });
      }
    }
    if (!result.isValid) {
      setErrors(newErrors);
      showErrorToast(translate(resources.products.add.validation.errorsInForm));
    }
    return result;
  };

  const saveInitialDetails = async () => {
    if (!partiallyValidate(false).isValid) return;

    const data = getAddProductCommand(
      AddProductSteps.ProductDetails,
      productType!,
      detailsForm,
      productOrServiceOfferDetailsForm,
      giftCardOfferDetailsForm,
      valueVoucherOfferDetailsForm,
      percentageVoucherOfferDetailsForm,
      productId,
    );

    setIsSaveButtonLoading(true);
    const resp = await productsService.addProduct(data);
    setIsSaveButtonLoading(false);

    if (!resp.hasErrors) {
      setActiveStep(AddProductSteps.OfferDetails);
      setProductId(resp.data);
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        setShowErrorPanel(true);
      }
    }
  };

  const saveProductOrServiceOfferDetails = async () => {
    if (!productId) return;

    const data = getAddProductCommand(
      AddProductSteps.OfferDetails,
      productType!,
      detailsForm,
      productOrServiceOfferDetailsForm,
      giftCardOfferDetailsForm,
      valueVoucherOfferDetailsForm,
      percentageVoucherOfferDetailsForm,
      productId,
    );

    setIsSaveButtonLoading(true);
    const resp = await productsService.addProduct(data);
    setIsSaveButtonLoading(false);

    if (!resp.hasErrors) {
      setActiveStep(AddProductSteps.Translation);
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        setShowErrorPanel(true);
      }
    }
  };

  const saveGiftCardOfferDetails = async () => {
    if (!productId) return;

    const data = getAddProductCommand(
      AddProductSteps.OfferDetails,
      productType!,
      detailsForm,
      productOrServiceOfferDetailsForm,
      giftCardOfferDetailsForm,
      valueVoucherOfferDetailsForm,
      percentageVoucherOfferDetailsForm,
      productId,
    );

    setIsSaveButtonLoading(true);
    const resp = await productsService.addProduct(data);
    setIsSaveButtonLoading(false);

    if (!resp.hasErrors) {
      setActiveStep(AddProductSteps.Translation);
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        setShowErrorPanel(true);
      }
    }
  };

  const saveValueVoucherOfferDetails = async () => {
    if (!productId) return;

    const data = getAddProductCommand(
      AddProductSteps.OfferDetails,
      productType!,
      detailsForm,
      productOrServiceOfferDetailsForm,
      giftCardOfferDetailsForm,
      valueVoucherOfferDetailsForm,
      percentageVoucherOfferDetailsForm,
      productId,
    );

    setIsSaveButtonLoading(true);
    const resp = await productsService.addProduct(data);
    setIsSaveButtonLoading(false);

    if (!resp.hasErrors) {
      setActiveStep(AddProductSteps.Translation);
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        setShowErrorPanel(true);
      }
    }
  };

  const savePercentageVoucherOfferDetails = async () => {
    if (!productId) return;

    if (_.isNil(percentageVoucherOfferDetailsForm.randomCode)) {
      setErrors(
        produce(d => {
          d.randomCode = [
            {
              code: resources.validation.required,
              target: 'randomCode',
              severity: 'error',
              placeholders: {},
            },
          ];
        }),
      );
      return;
    }

    const data = getAddProductCommand(
      AddProductSteps.OfferDetails,
      productType!,
      detailsForm,
      productOrServiceOfferDetailsForm,
      giftCardOfferDetailsForm,
      valueVoucherOfferDetailsForm,
      percentageVoucherOfferDetailsForm,
      productId,
    );

    setIsSaveButtonLoading(true);
    const resp = await productsService.addProduct(data);
    setIsSaveButtonLoading(false);

    if (!resp.hasErrors) {
      setActiveStep(AddProductSteps.Translation);
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        setShowErrorPanel(true);
      }
    }
  };

  const saveProductEnglishDetails = async (sendForReview: boolean) => {
    if (!productId) return;

    if (sendForReview) {
      const { isValid, errors } = validate(
        detailsForm,
        ProductContext.Add,
        productType!,
        productOrServiceOfferDetailsForm,
        giftCardOfferDetailsForm,
        percentageVoucherOfferDetailsForm,
        valueVoucherOfferDetailsForm,
      );
      if (!isValid) {
        showErrorToast(
          translate(resources.products.add.validation.errorsInForm),
        );
        setErrors(errors);
        return;
      }
    } else {
      if (!partiallyValidate(true).isValid) return;
    }

    const data = getAddProductCommand(
      AddProductSteps.Translation,
      productType!,
      detailsForm,
      productOrServiceOfferDetailsForm,
      giftCardOfferDetailsForm,
      valueVoucherOfferDetailsForm,
      percentageVoucherOfferDetailsForm,
      productId,
      sendForReview,
    );

    setIsSendForReviewButtonLoading(sendForReview);
    setIsSaveAsDraftButtonLoading(!sendForReview);
    const resp = await productsService.addProduct(data);
    setIsSendForReviewButtonLoading(false);
    setIsSaveAsDraftButtonLoading(false);

    if (!resp.hasErrors) {
      if (sendForReview) {
        setShowConfirmationDialog(true);
        setProductSentForReviewInfoMessage(
          resources.products.list.sentForReview,
          {
            0: detailsForm.productDescriptions.ro.name.value!,
          },
          accountInfo.manufacturerId,
        );
      } else {
        showSuccessToast(translate(resources.products.add.success));
        navigate('/products');
      }
    } else {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        setShowErrorPanel(true);
      }
    }
  };

  if (showErrorPanel) return <ErrorPanel />;

  const previewData = {
    productTypes: productTypes!,
    producyType: productType!,
    status: ProductStatusTypes.Draft,
    imageContent: detailsForm.imageContent!,
    name: detailsForm.productDescriptions.ro.name!.value!,
    productStatuses: productStatuses!,
  };

  return (
    <>
      <ProgressBar activeStep={activeStep} />
      <Loader loading={showLoading} loader={<LoadingSpinner />}>
        {activeStep === AddProductSteps.ProductType && (
          <ProductTypeSelector
            productTypes={productTypes}
            productType={productType}
            setProductType={setProductType}
            onBackClick={() => navigate(-1)}
            onContinueClick={() =>
              setActiveStep(AddProductSteps.ProductDetails)
            }
          />
        )}
        {activeStep === AddProductSteps.ProductDetails && (
          <ProductDetails
            productId={productId}
            previewData={previewData}
            countiesWithCities={countiesWithCities!}
            detailsForm={detailsForm}
            isSaveButtonLoading={isSaveButtonLoading}
            errors={errors}
            setErrors={setErrors}
            setDetailsForm={setDetailsForm}
            buttonFunctions={{
              onBackClick: () => setActiveStep(AddProductSteps.ProductType),
              onContinueClick: saveInitialDetails,
            }}
            showContactDetailsFields
          />
        )}
        {activeStep === AddProductSteps.OfferDetails &&
          productType === ProductTypes.ProductOrService && (
            <ProductOrServiceOfferDetails
              previewData={previewData}
              isSaveButtonLoading={isSaveButtonLoading}
              offerDetailsForm={productOrServiceOfferDetailsForm}
              setOfferDetailsForm={setProductOrServiceOfferDetailsForm}
              errors={errors}
              setErrors={setErrors}
              buttonFunctions={{
                onBackClick: () =>
                  setActiveStep(AddProductSteps.ProductDetails),
                onContinueClick: saveProductOrServiceOfferDetails,
              }}
              onlyAddCodes={false}
              context={ProductContext.Add}
            />
          )}
        {activeStep === AddProductSteps.OfferDetails &&
          productType === ProductTypes.GiftCard && (
            <GiftCardOfferDetails
              previewData={previewData}
              isSaveButtonLoading={isSaveButtonLoading}
              offerDetailsForm={giftCardOfferDetailsForm}
              setOfferDetailsForm={setGiftCardOfferDetailsForm}
              errors={errors}
              setErrors={setErrors}
              buttonFunctions={{
                onBackClick: () =>
                  setActiveStep(AddProductSteps.ProductDetails),
                onContinueClick: saveGiftCardOfferDetails,
              }}
              onlyAddCodes={false}
              context={ProductContext.Add}
            />
          )}
        {activeStep === AddProductSteps.OfferDetails &&
          productType === ProductTypes.ValueVoucher && (
            <ValueVoucherOfferDetails
              previewData={previewData}
              isSaveButtonLoading={isSaveButtonLoading}
              offerDetailsForm={valueVoucherOfferDetailsForm}
              setOfferDetailsForm={setValueVoucherOfferDetailsForm}
              errors={errors}
              setErrors={setErrors}
              buttonFunctions={{
                onBackClick: () =>
                  setActiveStep(AddProductSteps.ProductDetails),
                onContinueClick: saveValueVoucherOfferDetails,
              }}
            />
          )}
        {activeStep === AddProductSteps.OfferDetails &&
          productType === ProductTypes.PercentageVoucher && (
            <PercentageVoucherOfferDetails
              previewData={previewData}
              isSaveButtonLoading={isSaveButtonLoading}
              offerDetailsForm={percentageVoucherOfferDetailsForm}
              setOfferDetailsForm={setPercentageVoucherOfferDetailsForm}
              errors={errors}
              setErrors={setErrors}
              buttonFunctions={{
                onBackClick: () =>
                  setActiveStep(AddProductSteps.ProductDetails),
                onContinueClick: savePercentageVoucherOfferDetails,
              }}
            />
          )}
        {activeStep === AddProductSteps.Translation && (
          <Translations
            detailsForm={detailsForm}
            setDetailsForm={setDetailsForm}
            isSaveAsDraftButtonLoading={isSaveAsDraftButtonLoading}
            isSendForReviewButtonLoading={isSendForReviewButtonLoading}
            buttonFunctions={{
              onBackClick: () => setActiveStep(AddProductSteps.OfferDetails),
              onSaveClick: saveProductEnglishDetails,
            }}
            errors={errors}
            setErrors={setErrors}
            showContactDetailsFields
          />
        )}
      </Loader>
      {showConfirmationDialog && <ConfirmationDialog />}
    </>
  );
};

const ConfirmationDialog = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const closeDialog = () => navigate('/products');

  return (
    <Dialog showCloseIcon closeDialog={closeDialog} className="dialog-s">
      <div className="dialog-content">
        <div className="dialog-image m-dialog-img">
          <img
            src={ModalCheckImg}
            alt={translate(resources.products.add.sentForReviewDialog.title)}
          />
        </div>
        <div className="dialog-title">
          {translate(resources.products.add.sentForReviewDialog.title)}
        </div>
        <div className="dialog-text centered">
          {translate(resources.products.add.sentForReviewDialog.info)}
        </div>
      </div>
      <div className="dialog-actions vertical">
        <Button onClick={closeDialog} className="btn btn-solid">
          <span className="btn-text">{translate(resources.understood)}</span>
        </Button>
      </div>
    </Dialog>
  );
};
